import React from 'react';

import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import AppStack, { type AppStackItem, type AppStackItemProps } from '@atlassian/app-stack';

import type { SuggestionSource } from '../../../aiSuggestionsPluginType';
import { getSuggestionSourceApps, SourceAppIcon, type SuggestionSourceApp } from './sourceApps';

export const SUGGESTION_SOURCE_APP_STACK_TESTID = 'ai-suggestion-source-app-stack';

/**
 * Caps how many logos are shown. The design shows two; a third leaves room for a genuinely
 * multi-app suggestion without the stack crowding the Details row it shares with the
 * Restricted lozenge.
 */
const MAX_VISIBLE_APPS = 3;

/** Binds an icon to its app for `AppStack`, cached by key so the component identity stays stable. */
const iconCache = new Map<string, React.ComponentType<AppStackItemProps>>();

const getAppStackIcon = (app: SuggestionSourceApp): React.ComponentType<AppStackItemProps> => {
	const cached = iconCache.get(app.key);

	if (cached) {
		return cached;
	}

	const Icon = (): React.JSX.Element => <SourceAppIcon app={app} size="xsmall" />;

	Icon.displayName = `${app.key}AppStackIcon`;
	iconCache.set(app.key, Icon);

	return Icon;
};

export type SuggestionSourceAppStackProps = {
	sources: SuggestionSource[] | undefined;
};

/** De-duplicated icons for the apps a suggestion's sources came from; unattributed ones show a globe. */
export function SuggestionSourceAppStack({
	sources,
}: SuggestionSourceAppStackProps): React.JSX.Element | null {
	const { formatList, formatMessage } = useIntl();
	const sourceApps = React.useMemo(() => getSuggestionSourceApps(sources), [sources]);
	const data: AppStackItem[] = React.useMemo(
		() => sourceApps.map((app) => ({ component: getAppStackIcon(app), label: '' })),
		[sourceApps],
	);

	if (data.length === 0) {
		return null;
	}

	const sourceAppsLabel = formatMessage(aiSuggestionsMessages.cardSourceAppsTooltip, {
		apps: formatList(sourceApps.map(({ name }) => name)),
	});

	return (
		<Tooltip content={sourceAppsLabel} position="top" ignoreTooltipPointerEvents>
			<AppStack
				data={data}
				// `xsmall` is the design's 20px tile, and gives the -4px overlap it shows.
				size="xsmall"
				maxCount={MAX_VISIBLE_APPS}
				label={sourceAppsLabel}
				testId={SUGGESTION_SOURCE_APP_STACK_TESTID}
			/>
		</Tooltip>
	);
}
